import React, {memo, type FC} from 'react'
import remarkGfm from 'remark-gfm'
import ReactMarkdown from 'react-markdown'
import '@assistant-ui/react-markdown/styles/dot.css'
import type {TextMessagePartComponent, TextMessagePartProps} from '@assistant-ui/react'
import {useMessage, useMessagePartRuntime} from '@assistant-ui/react'
import {getOmicsAssistantMessage} from './getOmicsAssistantMessage'
import {
  chatInlineMarkdownComponents,
  prepareCloudMarkdown,
  markdownUrlTransform,
} from './agentStepMarkdown'
import {buildOmicsMarkdownComponents} from './omicsMarkdownCodePipeline'
import {OmicsShikiHighlighter} from './omicsShikiHighlighter'
import {OmicsCodeBlockHeader} from './omicsCodeBlockHeader'
import clsx from 'clsx'

const markdownComponents = buildOmicsMarkdownComponents({
  ...chatInlineMarkdownComponents,
  SyntaxHighlighter: OmicsShikiHighlighter,
  CodeHeader: OmicsCodeBlockHeader,
})

export const OmicsMarkdownBody: FC<{markdown: string; className?: string}> = ({markdown, className}) => (
  <div className={clsx('markdown-content', className)}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} urlTransform={markdownUrlTransform} components={markdownComponents}>
      {prepareCloudMarkdown(markdown)}
    </ReactMarkdown>
  </div>
)

/** assistant-ui text part: markdown + syntax highlighting + final-lane styling when appropriate. */
export const OmicsAssistantTextPart: TextMessagePartComponent = memo((props: TextMessagePartProps) => {
  const {text} = props
  const omics = useMessage((s) => getOmicsAssistantMessage(s))
  const content = useMessage((s) => s.content)
  const partRt = useMessagePartRuntime({optional: true})

  let isFinalLane = false
  if (omics?.timeline?.schema_version === 2 && partRt) {
    const sel = partRt.path.messagePartSelector
    const idx = sel.type === 'index' ? sel.index : -1
    const lastIdx = content.length - 1
    const last = content[lastIdx] as {type?: string} | undefined
    isFinalLane = idx === lastIdx && last?.type === 'text'
  }

  return (
    <OmicsMarkdownBody
      markdown={text}
      className={isFinalLane ? 'final-answer ai-chat-final-lane' : undefined}
    />
  )
})

OmicsAssistantTextPart.displayName = 'OmicsAssistantTextPart'
